// src/App.js
import React, { useState, useEffect, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Outlet, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import AOS from "aos";
import "aos/dist/aos.css";
import OptimizedImage from "./Components/OptimizedImage";
import ScrollToTop from "./Components/ScrollToTop";

// Critical components - load immediately
import SubMenu from "./Components/SubMenu";
import "./App.css";
import Navigation from "./Components/Navigation";
import { accordionData } from "./data/data";

// ─── Skeleton Styles ────────────────────────────────────────────────────────
const skeletonStyles = `
  @keyframes shimmer {
    0%   { background-position: -800px 0; }
    100% { background-position:  800px 0; }
  }

  .skeleton-base {
    background: linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%);
    background-size: 800px 100%;
    animation: shimmer 1.4s infinite linear;
    border-radius: 6px;
  }

  .skeleton-wrapper {
    padding: 32px 24px;
    width: 100%;
    box-sizing: border-box;
  }

  /* Hero / full-width block */
  .skeleton-hero {
    width: 100%;
    height: 420px;
    border-radius: 12px;
    margin-bottom: 24px;
  }

  /* Card grid */
  .skeleton-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 20px;
    margin-bottom: 24px;
  }
  .skeleton-card {
    border-radius: 10px;
    overflow: hidden;
    background: #fff;
    box-shadow: 0 2px 8px rgba(0,0,0,.06);
  }
  .skeleton-card-img   { height: 160px; width: 100%; }
  .skeleton-card-body  { padding: 14px; }
  .skeleton-card-title { height: 18px; width: 70%; margin-bottom: 10px; }
  .skeleton-card-line  { height: 13px; width: 90%; margin-bottom: 8px; }
  .skeleton-card-line.short { width: 55%; }

  /* Text block */
  .skeleton-text-block { margin-bottom: 24px; }
  .skeleton-heading { height: 28px; width: 45%; margin-bottom: 16px; }
  .skeleton-line    { height: 14px; width: 100%; margin-bottom: 10px; }
  .skeleton-line.w80 { width: 80%; }
  .skeleton-line.w60 { width: 60%; }

  /* Map placeholder */
  .skeleton-map {
    width: 100%;
    height: 380px;
    border-radius: 12px;
    margin-bottom: 24px;
  }

  /* Form */
  .skeleton-form { max-width: 560px; }
  .skeleton-field {
    height: 48px;
    width: 100%;
    margin-bottom: 16px;
    border-radius: 8px;
  }
  .skeleton-btn {
    height: 44px;
    width: 140px;
    border-radius: 8px;
  }

  /* Testimonials row */
  .skeleton-testimonials {
    display: flex;
    gap: 20px;
    overflow: hidden;
    margin-bottom: 24px;
  }
  .skeleton-testimonial-card {
    flex: 0 0 280px;
    padding: 20px;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 2px 8px rgba(0,0,0,.06);
  }
  .skeleton-avatar {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    margin-bottom: 14px;
  }

  /* Minister grid */
  .skeleton-minister-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 20px;
  }
  .skeleton-minister-card { border-radius: 10px; overflow: hidden; }
  .skeleton-minister-img  { height: 220px; width: 100%; }
  .skeleton-minister-name { height: 16px; width: 65%; margin: 12px auto 8px; }
  .skeleton-minister-role { height: 12px; width: 45%; margin: 0 auto 12px; }
`;

// ─── Reusable Skeleton Primitives ────────────────────────────────────────────
function S({ className = "", style = {} }) {
  return <div className={`skeleton-base ${className}`} style={style} />;
}

// ─── Contextual Skeleton Screens ─────────────────────────────────────────────

/** Generic page: heading + lines + cards */
function PageSkeleton() {
  return (
    <div className="skeleton-wrapper">
      <S className="skeleton-heading" />
      {[...Array(3)].map((_, i) => (
        <S key={i} className={`skeleton-line ${i === 2 ? "w60" : i === 1 ? "w80" : ""}`} />
      ))}
      <div className="skeleton-grid" style={{ marginTop: 28 }}>
        {[...Array(6)].map((_, i) => (
          <div key={i} className="skeleton-card">
            <S className="skeleton-card-img" />
            <div className="skeleton-card-body">
              <S className="skeleton-card-title" />
              <S className="skeleton-card-line" />
              <S className="skeleton-card-line short" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Hero-style loading (home sections) */
function HeroSkeleton() {
  return (
    <div className="skeleton-wrapper">
      <S className="skeleton-hero" />
      <div style={{ display: "flex", gap: 16 }}>
        {[...Array(3)].map((_, i) => (
          <S key={i} style={{ flex: 1, height: 60, borderRadius: 8 }} />
        ))}
      </div>
    </div>
  );
}

/** Map skeleton */
function MapSkeleton() {
  return (
    <div className="skeleton-wrapper">
      <S className="skeleton-heading" style={{ width: "30%" }} />
      <S className="skeleton-map" />
    </div>
  );
}

/** Form skeleton */
function FormSkeleton() {
  return (
    <div className="skeleton-wrapper">
      <S className="skeleton-heading" style={{ width: "40%", marginBottom: 24 }} />
      <div className="skeleton-form">
        {[...Array(5)].map((_, i) => <S key={i} className="skeleton-field" />)}
        <S className="skeleton-btn" />
      </div>
    </div>
  );
}

/** Testimonials skeleton */
function TestimonialsSkeleton() {
  return (
    <div className="skeleton-wrapper">
      <S className="skeleton-heading" style={{ width: "35%", marginBottom: 20 }} />
      <div className="skeleton-testimonials">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="skeleton-testimonial-card">
            <S className="skeleton-avatar" />
            <S style={{ height: 14, width: "80%", marginBottom: 8 }} />
            <S style={{ height: 14, width: "60%", marginBottom: 8 }} />
            <S style={{ height: 14, width: "70%" }} />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Ministers grid skeleton */
function MinistersSkeleton() {
  return (
    <div className="skeleton-wrapper">
      <S className="skeleton-heading" style={{ width: "30%", marginBottom: 24 }} />
      <div className="skeleton-minister-grid">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="skeleton-minister-card">
            <S className="skeleton-minister-img" />
            <S className="skeleton-minister-name" />
            <S className="skeleton-minister-role" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Party about-page skeleton */
function PartySkeleton() {
  return (
    <div className="skeleton-wrapper">
      <div style={{ display: "flex", gap: 32, alignItems: "flex-start" }}>
        <S style={{ width: 180, height: 180, borderRadius: "50%", flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <S className="skeleton-heading" style={{ width: "50%" }} />
          {[...Array(4)].map((_, i) => (
            <S key={i} className={`skeleton-line ${i === 3 ? "w60" : ""}`} />
          ))}
        </div>
      </div>
      <div className="skeleton-grid" style={{ marginTop: 32 }}>
        {[...Array(4)].map((_, i) => (
          <div key={i} className="skeleton-card">
            <S className="skeleton-card-img" />
            <div className="skeleton-card-body">
              <S className="skeleton-card-title" />
              <S className="skeleton-card-line" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Inject styles once ───────────────────────────────────────────────────────
function SkeletonStyleInjector() {
  useEffect(() => {
    const id = "skeleton-global-styles";
    if (!document.getElementById(id)) {
      const tag = document.createElement("style");
      tag.id = id;
      tag.textContent = skeletonStyles;
      document.head.appendChild(tag);
    }
  }, []);
  return null;
}

// ─── Retry-capable lazy loader ────────────────────────────────────────────────
const lazyWithRetry = (componentImport, retries = 3) => {
  return lazy(() => {
    return new Promise((resolve, reject) => {
      const retryImport = (attempt = 1) => {
        componentImport()
          .then(resolve)
          .catch((error) => {
            if (error.message && (error.message.includes("chunk") || error.message.includes("Loading chunk"))) {
              if (attempt < retries) {
                console.warn(`Chunk loading failed, retrying (${attempt}/${retries})...`, error);
                setTimeout(() => retryImport(attempt + 1), 1000 * attempt);
              } else {
                console.error("Chunk loading failed after retries, reloading page...", error);
                setTimeout(() => window.location.reload(), 2000);
              }
            } else {
              reject(error);
            }
          });
      };
      retryImport();
    });
  });
};

// ─── Lazy Imports ─────────────────────────────────────────────────────────────
const Testimonials            = lazyWithRetry(() => import("./Components/Testimonials"));
const ContactSection          = lazyWithRetry(() => import("./Components/contact"));
const HeroSection             = lazyWithRetry(() => import("./Components/Last"));
const MapToggle               = lazyWithRetry(() => import("./Components/MapToggle"));
const VideoSection            = lazyWithRetry(() => import("./Components/youtube"));
const GrievanceForm           = lazyWithRetry(() => import("./Components/GrievanceForm"));
const Privacypolicy           = lazyWithRetry(() => import("./Components/Privacypolicy"));
const ConstituencyGrid        = lazyWithRetry(() => import("./Components/mapgrid"));
const TDPPage                 = lazyWithRetry(() => import("./Components/about_tdp"));
const YSRCPPage               = lazyWithRetry(() => import("./Components/about_ysrcp"));
const JSPPage                 = lazyWithRetry(() => import("./Components/about_jsp"));
const CongressPage            = lazyWithRetry(() => import("./Components/about_congress"));
const CPIMPage                = lazyWithRetry(() => import("./Components/about_cpmi"));
const Bjp                     = lazyWithRetry(() => import("./Components/about_bjp"));
const ElectoralStrategies     = lazyWithRetry(() => import("./Components/ElectoralStrategies"));
const Complate                = lazyWithRetry(() => import("./Components/complaint"));
const ConstituencyDashboardForm = lazyWithRetry(() => import("./Components/ConstituencyDashboardForm"));
const ConstituencyList1       = lazyWithRetry(() => import("./Components/grid"));
const ConstituencyPage        = lazyWithRetry(() => import("./pages/ConstituencyPage"));
const ConstituencygridPage    = lazyWithRetry(() => import("./Components/constancyAboutPage"));
const LoginPage               = lazyWithRetry(() => import("./Components/Login_page"));
const TopContentBottom        = lazyWithRetry(() => import("./Components/aboutFairAndFiery"));
const MPWinnerForm            = lazyWithRetry(() => import("./Components/MPWinnerForm"));
const MinistersPage           = lazyWithRetry(() => import("./Components/MinistersPage"));
const MinisterDetailPage      = lazyWithRetry(() => import("./Components/MinisterDetailPage"));
const MinistersManagementPage = lazyWithRetry(() => import("./Components/MinistersManagementPage"));
const PartyStripeHover        = lazyWithRetry(() => import("./Components/PartyStripeHover"));
const Footer                  = lazyWithRetry(() => import("./Components/footer"));

// ─── Home ─────────────────────────────────────────────────────────────────────
function HomeContent({ active, setActive }) {
  return (
    <>
      <Helmet>
        <title>Fair & Fiery Poll Strategies – Andhra Pradesh Election Analysis & Political Insights</title>
        <meta name="description" content="Fair & Fiery — Election Analysis, Party History, Strategies, and Political Insights for Andhra Pradesh." />
        <meta property="og:title"       content="Fair & Fiery Poll Strategies – Andhra Pradesh Election Analysis" />
        <meta property="og:description" content="Election analysis, political party data, and strategy insights for Andhra Pradesh." />
        <meta property="og:url"         content="https://fairandfiery.com" />
        <meta property="og:type"        content="website" />
        <meta name="twitter:title"       content="Fair & Fiery Poll Strategies" />
        <meta name="twitter:description" content="Election analysis, political history, and strategy insights." />
        <meta name="twitter:card"        content="summary_large_image" />
      </Helmet>
<ScrollToTop />
      <Navigation data-aos="fade-down" />

      <Suspense fallback={<HeroSkeleton />}>
        <div data-aos="fade-up" data-aos-delay="100"><PartyStripeHover /></div>
      </Suspense>

      <Suspense fallback={<PageSkeleton />}>
        <div data-aos="fade-up" style={{ backgroundColor: "wheat" }}><TopContentBottom /></div>
      </Suspense>

      <Suspense fallback={<PageSkeleton />}>
        <div data-aos="fade-up" data-aos-delay="100"><ElectoralStrategies /></div>
      </Suspense>

      <Suspense fallback={<HeroSkeleton />}>
        <div data-aos="fade-up" data-aos-delay="200"><VideoSection /></div>
      </Suspense>

      <Suspense fallback={<MapSkeleton />}>
        <div data-aos="fade-up" data-aos-delay="300"><MapToggle /></div>
      </Suspense>

      <Suspense fallback={<TestimonialsSkeleton />}>
        <div data-aos="fade-up" data-aos-delay="400"><Testimonials /></div>
      </Suspense>

      <Suspense fallback={<FormSkeleton />}>
        <div data-aos="fade-up" data-aos-delay="500"><ContactSection /></div>
      </Suspense>

      <Suspense fallback={<HeroSkeleton />}>
        <div data-aos="fade-up" data-aos-delay="600"><HeroSection /></div>
      </Suspense>
    </>
  );
}

// ─── Layout ───────────────────────────────────────────────────────────────────
function Layout() {
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    AOS.init({ duration: 800, easing: "ease-in-out", once: true, offset: 100 });
    AOS.refresh();
  }, [location.pathname]);

  return (
    <div className="app-root">
      {!isHome && <SubMenu />}
      <main className="app-main">
        <Outlet />
      </main>
      <Suspense fallback={null}>
        <Footer />
      </Suspense>
    </div>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────
function App() {
  const [active, setActive] = useState(null);

  return (
    <Router>
      <SkeletonStyleInjector />
      <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<HomeContent active={active} setActive={setActive} />} />

          <Route path="/GrievanceForm"
            element={<Suspense fallback={<FormSkeleton />}><GrievanceForm /></Suspense>} />

          <Route path="/MPWinnerForm"
            element={<Suspense fallback={<FormSkeleton />}><MPWinnerForm /></Suspense>} />

          <Route path="/ministers"
            element={<Suspense fallback={<MinistersSkeleton />}><MinistersPage /></Suspense>} />

          <Route path="/minister/:filename"
            element={<Suspense fallback={<PageSkeleton />}><MinisterDetailPage /></Suspense>} />

          <Route path="/ministers/manage"
            element={<Suspense fallback={<FormSkeleton />}><MinistersManagementPage /></Suspense>} />

          <Route path="/privacy-policy"
            element={<Suspense fallback={<PageSkeleton />}><Privacypolicy /></Suspense>} />

          <Route path="/mapgrid"
            element={<Suspense fallback={<MapSkeleton />}><ConstituencyGrid /></Suspense>} />

          {/* Party pages */}
          {[
            ["/about_tdp",     <TDPPage />],
            ["/about_ysrcp",   <YSRCPPage />],
            ["/about_jsp",     <JSPPage />],
            ["/about_congress",<CongressPage />],
            ["/about_cpmi",    <CPIMPage />],
            ["/about_bjp",     <Bjp />],
          ].map(([path, el]) => (
            <Route key={path} path={path}
              element={<Suspense fallback={<PartySkeleton />}>{el}</Suspense>} />
          ))}

          <Route path="/constituencies"
            element={<Suspense fallback={<PageSkeleton />}><ConstituencyList1 /></Suspense>} />

          <Route path="/Complate"
            element={<Suspense fallback={<FormSkeleton />}><Complate /></Suspense>} />

          <Route path="/constituency/:slug"
            element={<Suspense fallback={<PageSkeleton />}><ConstituencyPage /></Suspense>} />

          <Route path="/ConstituencyDashboardForm"
            element={<Suspense fallback={<FormSkeleton />}><ConstituencyDashboardForm /></Suspense>} />

          <Route path="/grid"
            element={<Suspense fallback={<PageSkeleton />}><ConstituencyList1 /></Suspense>} />

          <Route path="/ConstituencyaboutPage"
            element={<Suspense fallback={<PageSkeleton />}><ConstituencygridPage /></Suspense>} />

          <Route path="/Login_page"
            element={<Suspense fallback={<FormSkeleton />}><LoginPage /></Suspense>} />
        </Route>
      </Routes>
    </Router>
  );
}

export default App;